import { Injectable } from '@angular/core';
import { FirebaseFunctionsService } from './firebaseFunctions.service';
import { ServiceAreasService } from './serviceAreas.service';
import { UtilsService } from './utils.service';
import type {
    TAddress,
    TServiceArea,
    TValidation
} from '@shared/models';
type TAddressComponent = 'street_number' | 'route' | 'postal_code' | 'locality';
type TComponent = {
    long_name: string;
    short_name: string;
    types: string[];
};

@Injectable({
    'providedIn': 'root'
})
export class AddressVerificationService {
    private serviceAreas: TServiceArea[] = [];

    constructor(private firebaseFunctionService: FirebaseFunctionsService, private utilsService: UtilsService,
        private serviceAreasService: ServiceAreasService) {
    }

    public async verifyAddress(addr: TAddress): Promise<TValidation> {
        this.serviceAreas = await this.serviceAreasService.getAll();

        // Make certain the street address doesn't have invalid chars.
        addr.streetAddress = this.utilsService.replaceCh(addr.streetAddress, '.#', '');

        const testAddress = this.utilsService.formatAddressForGoogleMaps(
            addr.streetAddress, addr.city, addr.regionName, addr.zipcode, addr.countryName);

        const response = await this.firebaseFunctionService.convertStreetAddressToLatLng(testAddress);
        const validation: TValidation = this.validateAddress(addr, response);

        return validation;
    }

    private validateAddress(requestedAddress: TAddress, response: any): TValidation {
        if (response.status === 'ZERO_RESULTS') {
            throw new Error('Street not found in this zipcode');
        }

        // const status = response.status;
        const result = response.results[0];
        const locationType = result.geometry.location_type;
        const addressComponents = result.address_components;

        // The user only typed in a street number, a street name and a zipcode.
        // Even though the firebase function returned a lot more information like city, county, country etc.
        // we only need to validate the three typed in values.
        const foundStreetNums = this.extractValuesFromAddressComponentsByType(addressComponents, 'street_number');
        const foundStreetNames = this.extractValuesFromAddressComponentsByType(addressComponents, 'route');
        const foundZipcodes = this.extractValuesFromAddressComponentsByType(addressComponents, 'postal_code');
        const foundCities = this.extractValuesFromAddressComponentsByType(addressComponents, 'locality');

        let foundAddress = '';
        if (foundStreetNums && foundStreetNames) {
            foundAddress = foundStreetNums.long_name + ' ' + foundStreetNames.long_name;
        }
        // The user typed in these values.
        const requestedZipcode = requestedAddress.zipcode.toString();
        const parts = requestedAddress.streetAddress.split(' ');
        // const requestedStreetNumber = parts[0];
        const requestedStreetNumber = parts[0] || '';
        // const requestedStreetName = addr.replace(requestedStreetNumber || '', '').trim().toString();

        // Remove the street number to be left with the street name
        const addr = requestedAddress.streetAddress.toString();
        const requestedStreetName = addr.replace(requestedStreetNumber, '').trim().toString();

        // Verify that the typed in values resulted in a good address.
        const streetNumberVerified = this.verifyValue(requestedStreetNumber, foundStreetNums);
        const streetNameVerified = this.verifyValue(requestedStreetName, foundStreetNames);
        const zipcodeVerified = this.verifyValue(requestedZipcode, foundZipcodes);

        //
        const zipcode1 = this.isZipcodeServiceable(foundZipcodes?.long_name ?? '');
        const zipcode2 = this.isZipcodeServiceable(requestedZipcode);
        const isZipcodeServiceable = zipcode1 && zipcode2;

        //
        let statusTitle = '';
        let statusMessage = '';
        let streetNamePartialMatch = false;
        let zipcodePartialMatch = false;

        if (!isZipcodeServiceable) {
            statusTitle = 'We do not service this zipcode.';
            statusMessage = requestedZipcode;
        } else if (!zipcodeVerified) {
            // statusTitle    = 'Address not found in zipcode ' + requestedZipcode;
            statusTitle = 'Address not found in zipcode';
            statusMessage = requestedZipcode;
            if (foundZipcodes) {
                zipcodePartialMatch = true;
                // statusMessage = 'Address was found in zipcode ' + foundZipcodes.long_name;
            }
        } else if (!streetNameVerified) {
            statusTitle = 'Address not found on this street or street not found in this zipcode.';
            statusMessage = requestedAddress.streetAddress;
            if (foundAddress) {
                streetNamePartialMatch = true;
            }
        } else if (!streetNumberVerified) {
            statusTitle = 'Street number not found on this street.';
            statusMessage = requestedAddress.streetAddress;
        } else if (streetNumberVerified && streetNameVerified) {
            statusTitle = 'Exact address found.';
            statusMessage = result.formatted_address;
            streetNamePartialMatch = false;
        } else if (streetNumberVerified || streetNameVerified) {
            statusTitle = 'Address not found; however, a nearby match was found. Do you want to use this address?';
            statusMessage = requestedAddress.streetAddress;
        } else {
            statusTitle = 'Address not found.';
            statusMessage = requestedAddress.streetAddress;
        }

        const validation: TValidation = {
            'requestedAddress': requestedAddress,
            'location': {
                'formattedAddress': result.formatted_address,
                'locationType': locationType,
                'coordinate': {
                    'latitude': result.geometry.location.lat,
                    'longitude': result.geometry.location.lng
                }
            },
            'foundAddress': foundAddress,
            'found': (streetNumberVerified && streetNameVerified && zipcodeVerified),
            'foundZipcode': foundZipcodes?.long_name ?? '',
            'foundCity': foundCities?.long_name ?? '',
            'statusTitle': statusTitle,
            'statusMessage': statusMessage,
            'streetNamePartialMatch': streetNamePartialMatch,
            'zipcodePartialMatch': zipcodePartialMatch
        };

        return validation;
    }

    // This method belongs in serviceAreas.service
    private isZipcodeServiceable(requestedZipcode: string): boolean {
        const item = this.serviceAreas.find((value) => (value.zipcode === requestedZipcode));
        return (item !== undefined);
    }

    private extractValuesFromAddressComponentsByType(components: TAddressComponent, type: TAddressComponent): TComponent | undefined {
        let comp;
        if (Array.isArray(components)) {
            // let idx = -1;
            components.forEach((component) => {
                // ++idx;
                const types = component.types;
                types.forEach((typ: any) => {
                    if (typ === type) {
                        comp = component;
                    }
                });
            });
        }
        return comp;
    }

    private verifyValue(testValue: string, acceptableValues: TComponent | undefined): boolean {
        // Assume the user types in the address for Costco:  3411 St Rose Parkway
        // The firebase function returns short name:  3411 St Rose Pkwy and long name: 3411 Saint Rose Parkway
        // The user doesn't realize he typed in a combination of short and long names.

        if (!testValue || !acceptableValues || !acceptableValues.long_name || !acceptableValues.short_name) {
            return false;
        }

        const testwords = testValue.toLowerCase().split(' ');

        const acceptablevalue1 = acceptableValues.long_name.toLowerCase();
        const acceptablevalue2 = acceptableValues.short_name.toLowerCase();

        const allAcceptableValues = acceptablevalue1 + ' ' + acceptablevalue2;
        const words: string[] = allAcceptableValues.split(' ');

        let allTestWordsAcceptable = true;
        testwords.forEach((word) => {
            const found = words.includes(word);
            if (!found) {
                allTestWordsAcceptable = false;
            }
        });
        // words.includes(testvalue);
        // const foundWord = words.includes(testvalue);

        return allTestWordsAcceptable;

        // Do not do this:
        // Search for encia in the string = 'valencia boulevard valencia blvd'
        // The above string search will return true.  Obviously there is no street named encia.
    }
}
